import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { API_ORIGIN, ApiError, api, patch, post, put } from "./api";
import { applyDevicePreset, createDefaultFingerprintConfig, ProfileConfigurationEditor, type DevicePreset, type JsonObject } from "./ProfileConfigurationEditor";
import P2PPage from "./P2PPage";
import DataTable from "./DataTable";
import Modal, { requestModalClose } from "./Modal";
import Pagination from "./Pagination";
import { useUrlState } from "./urlState";
import { useLiveResource as useLoad } from "./useLiveResource";
import SectionNav from "./SectionNav";
import CommercialApp, { commercialEnabled, SubscriptionBanner, WorkspaceMembers, WorkspaceTrash } from "./commercial";

type Me = { user_id: string; username: string; email: string; display_name: string; role: string; session_id: string; commercial?: any };
type PageId = "dashboard" | "p2p" | "accounts" | "devices" | "profiles" | "groups" | "revisions" | "leases" | "recovery" | "sessions" | "releases" | "proxies" | "audit" | "system" | "health" | "trash";
type Page<T> = { items: T[]; limit: number; offset: number; total?: number };
type LoginStep = { next: string; flow_token: string; recovery_codes?: string[] | null };

const fmtDate = (value?: string | null) => value ? new Intl.DateTimeFormat("ru-RU", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—";
const fmtBytes = (value?: number | null) => {
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value, unit = 0;
  while (size >= 1024 && unit < units.length - 1) { size /= 1024; unit++; }
  return `${size.toFixed(unit ? 1 : 0)} ${units[unit]}`;
};

function Brand() {
  return <div className="brand"><span>◆</span><strong>GShard</strong><em>Сервер</em></div>;
}

const valueLabels: Record<string, string> = {
  ACTIVE: "Активен", BLOCKED: "Заблокирован", DISABLED: "Отключён", PENDING: "Ожидает", TRUSTED: "Доверенный", REVOKED: "Отозван", DENIED: "Отклонён",
  PASSWORD_RESET_REQUIRED: "Требуется смена пароля", MFA_SETUP_REQUIRED: "Требуется настройка MFA", RECOVERY_REQUIRED: "Требуется восстановление", RELEASING: "Завершение", EXPIRED: "Истёк",
  DRAFT: "Черновик", PUBLISHED: "Опубликован", SUPERSEDED: "Заменён", ARCHIVED: "В архиве", QUARANTINED: "Карантин", RECOVERY: "Восстановление", RESTORE: "Восстановлено", IMPORT: "Импорт", CLIENT_SYNC: "Синхронизация клиента", ADMIN_UPLOAD: "Загрузка администратором", SUCCESS: "Успешно", FAILED: "Ошибка", APPROVED: "Одобрен", ACCEPTED: "Принят", DISCARDED: "Отклонён", CLONED: "Клонирован", RELEASED: "Завершён",
  USER: "Пользователь", ADMIN: "Администратор", SUPERADMIN: "Суперадминистратор", EVERYONE: "Все пользователи", AUTO: "Автоматически", APPROVAL_REQUIRED: "С подтверждением", ACTIVATION_CODE: "По коду активации",
  WINDOWS: "Windows", LINUX: "Linux", MACOS: "macOS", STABLE: "Стабильный", BETA: "Бета", INTERNAL: "Внутренний",
  APPROVE: "Одобрить", DENY: "Отклонить", BLOCK: "Заблокировать", REVOKE: "Отозвать",
  ok: "Готово", unavailable: "Недоступно",
};
const valueLabel = (value: ReactNode) => typeof value === "string" ? valueLabels[value] ?? value : value;

function ErrorBox({ error, onClose }: { error: string; onClose?: () => void }) {
  const [copiedError, setCopiedError] = useState("");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(error);
      setCopiedError(error);
    } catch {
      setCopiedError("");
    }
  };
  return <div className="alert error"><b>!</b><span className="alert-message">{error}</span><div className="alert-actions"><button type="button" onClick={copy}>{copiedError === error ? "Скопировано" : "Копировать"}</button>{onClose && <button type="button" aria-label="Закрыть" onClick={onClose}>×</button>}</div></div>;
}

function ServerConnection() {
  const [state, setState] = useState<"checking" | "online" | "offline">("checking");
  const [details, setDetails] = useState("Проверка доступности сервера");

  useEffect(() => {
    let active = true;
    let controller: AbortController | null = null;
    const check = async () => {
      controller?.abort();
      controller = new AbortController();
      try {
        const response = await fetch(`${API_ORIGIN}/health/ready`, {
          cache: "no-store",
          credentials: "include",
          signal: controller.signal,
        });
        if (!active) return;
        setState(response.ok ? "online" : "offline");
        setDetails(response.ok ? "Сервер отвечает и готов принимать запросы" : `Сервер вернул ошибку HTTP ${response.status}`);
      } catch (reason) {
        if (!active || (reason instanceof DOMException && reason.name === "AbortError")) return;
        setState("offline");
        setDetails("Нет связи с сервером. Проверьте сеть или состояние Server GShard.");
      }
    };
    void check();
    const timer = window.setInterval(check, 15_000);
    window.addEventListener("online", check);
    return () => {
      active = false;
      controller?.abort();
      window.clearInterval(timer);
      window.removeEventListener("online", check);
    };
  }, []);

  const label = state === "online" ? "Сервер доступен" : state === "offline" ? "Сервер недоступен" : "Проверка сервера…";
  return <div className={`connection ${state}`} title={details}><i />{label}</div>;
}

function Login({ onLogin }: { onLogin: (me: Me) => void }) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [step, setStep] = useState<LoginStep | null>(null);
  const [setup, setSetup] = useState<any>(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const accept = async (next: LoginStep) => {
    setStep(next); setCode("");
    if (next.next === "MFA_SETUP_REQUIRED") setSetup(await post("/api/v1/auth/mfa/setup", { flow_token: next.flow_token }));
    if (next.next === "AUTHENTICATED") {
      if (next.recovery_codes?.length) setCodes(next.recovery_codes);
      else onLogin(await api<Me>("/api/v1/auth/me"));
    }
  };
  const run = async (action: () => Promise<LoginStep>) => {
    setBusy(true); setError("");
    try { await accept(await action()); } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { setBusy(false); }
  };
  const credentials = (event: FormEvent) => { event.preventDefault(); run(() => post("/api/v1/auth/login", { login, password, web: true })); };
  const changePassword = (event: FormEvent) => { event.preventDefault(); run(() => post("/api/v1/auth/flow/change-password", { flow_token: step?.flow_token, current_password: password, new_password: newPassword })); };
  const verify = (event: FormEvent) => { event.preventDefault(); run(() => post("/api/v1/auth/mfa/verify", { flow_token: step?.flow_token, code: recovery ? null : code, recovery_code: recovery ? code : null })); };
  const finishCodes = async () => onLogin(await api<Me>("/api/v1/auth/me"));

  return <div className="login-shell"><main className="login-card"><Brand />
    {codes ? <>
      <p className="eyebrow good">MFA включена</p><h1>Сохраните коды восстановления</h1><p className="sub">Они показываются только один раз. Сохраните их в одобренном организацией менеджере паролей.</p>
      <div className="recovery-codes">{codes.map((item) => <code key={item}>{item}</code>)}</div>
      <button className="secondary" onClick={async () => { try { await navigator.clipboard.writeText(codes.join("\n")); setSaved(true); setError(""); } catch { setError("Не удалось скопировать коды. Выделите их и сохраните вручную."); } }}>{saved ? "Скопировано" : "Копировать коды"}</button>
      {error && <ErrorBox error={error} />}
      <label className="check"><input type="checkbox" checked={saved} onChange={(event) => setSaved(event.target.checked)} />Я сохранил эти коды</label>
      <button className="primary" disabled={!saved} onClick={finishCodes}>Открыть панель управления</button>
    </> : step?.next === "PASSWORD_CHANGE_REQUIRED" ? <>
      <p className="eyebrow">Первый вход</p><h1>Замените временный пароль</h1><p className="sub">Используйте не менее 12 символов и уникальный пароль.</p>
      <form onSubmit={changePassword}><Field label="Временный пароль" type="password" value={password} onChange={setPassword} /><Field label="Новый пароль" type="password" value={newPassword} onChange={setNewPassword} />{error && <ErrorBox error={error} />}<button className="primary" disabled={busy}>{busy ? "Обновление…" : "Обновить пароль"}</button></form>
    </> : step?.next === "MFA_REQUIRED" || step?.next === "MFA_SETUP_REQUIRED" ? <>
      <p className="eyebrow">Проверка входа</p><h1>{setup ? "Настройка приложения-аутентификатора" : "Введите код TOTP"}</h1>
      {setup ? <div className="qr"><img src={setup.qr_data_url} alt="QR-код TOTP" /><div><small>Ключ для ручного ввода</small><code>{setup.secret}</code></div></div> : <p className="sub">Подтвердите вход с помощью второго фактора.</p>}
      <form onSubmit={verify}><Field label={recovery ? "Код восстановления" : "6-значный код"} value={code} onChange={setCode} autoFocus />{!setup && <button type="button" className="link" onClick={() => { setRecovery(!recovery); setCode(""); }}>{recovery ? "Использовать TOTP" : "Использовать код восстановления"}</button>}{error && <ErrorBox error={error} />}<button className="primary" disabled={busy || !code}>{busy ? "Проверка…" : "Подтвердить"}</button></form>
    </> : <>
      <p className="eyebrow">Защищённая панель</p><h1>Вход в GShard</h1><p className="sub">Используйте свою учётную запись GShard. После входа откроется доступное вам рабочее пространство.</p>
      <form onSubmit={credentials}><Field label="Имя пользователя или почта" value={login} onChange={setLogin} autoFocus /><Field label="Пароль" type="password" value={password} onChange={setPassword} />{error && <ErrorBox error={error} />}<button className="primary" disabled={busy || !login || !password}>{busy ? "Вход…" : "Продолжить"}</button></form>
    </>}
  </main></div>;
}

function Field({ label, value, onChange, type = "text", autoFocus = false, placeholder = "" }: { label: string; value: string; onChange: (value: string) => void; type?: string; autoFocus?: boolean; placeholder?: string }) {
  return <label className="field"><span>{label}</span><input type={type} value={value} onChange={(event) => onChange(event.target.value)} autoFocus={autoFocus} placeholder={placeholder} /></label>;
}

function Badge({ children, tone }: { children: ReactNode; tone?: string }) { return <span className={`badge ${tone ?? String(children).toLowerCase()}`}>{valueLabel(children)}</span>; }

function Panel({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return <section className="panel"><header><h2>{title}</h2>{action}</header>{children}</section>;
}

function usePaged<T extends Page<any>>(path: string, key: string) {
  const [storedOffset, setStoredOffset] = useUrlState(`${key}.offset`, "0");
  const previousPath = useRef(path);
  const offset = previousPath.current === path && Number.isSafeInteger(Number(storedOffset)) ? Math.max(0, Number(storedOffset)) : 0;
  useEffect(() => {
    if (previousPath.current !== path) { previousPath.current = path; setStoredOffset("0"); }
  }, [path, setStoredOffset]);
  const result = useLoad<T>(`${path}${path.includes("?") ? "&" : "?"}limit=100&offset=${offset}`, true);
  const page = result.data;
  return { ...result, pagination: page && <Pagination offset={page.offset} limit={page.limit} count={page.items.length} total={page.total} busy={result.loading} onChange={(next) => setStoredOffset(String(next))} /> };
}

function useDebouncedValue<T>(value: T, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => { const timer = window.setTimeout(() => setDebounced(value), delay); return () => window.clearTimeout(timer); }, [value, delay]);
  return debounced;
}

async function fetchAllPages<T>(path: string, subject = "записей") {
  const items: T[] = [];
  let offset = 0;
  while (true) {
    const separator = path.includes("?") ? "&" : "?";
    const page = await api<Page<T>>(`${path}${separator}limit=100&offset=${offset}`);
    items.push(...page.items);
    if (items.length > 10_000) throw new Error(`Найдено более 10 000 ${subject}. Уточните фильтры или выполните операцию через API.`);
    if (page.items.length < page.limit) break;
    offset += page.items.length;
  }
  return items;
}

function useAllPages<T>(path: string, subject = "записей") {
  const [data, setData] = useState<T[] | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try { setData(await fetchAllPages<T>(path, subject)); setError(""); }
    catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
  }, [path, subject]);
  useEffect(() => { load(); }, [load]);
  return { data, error, load };
}

function Dashboard() {
  const { data, error, load } = useLoad<any>("/api/v1/admin/dashboard", true);
  if (error && !data) return <ErrorBox error={error} />;
  if (!data) return <Loader />;
  const cards = [
    ["Активные аккаунты", data.accounts.active, "Пользователи с доступом к системе"],
    ["Ожидают настройки", data.accounts.onboarding, "Завершение входа или защиты аккаунта"],
    ["Заблокированы", data.accounts.blocked, "Доступ ограничен"],
    ["Доверенные устройства", data.devices.trusted, `${data.devices.pending} ожидают подтверждения`],
  ];
  return <>
    <PageHead title="Обзор" subtitle="Главное по аккаунтам, устройствам и запущенным профилям." action={<button className="secondary compact" onClick={load}>Обновить</button>} />
    {error && <ErrorBox error={error} />}
    <div className="live-status"><i />Автообновление каждые 15 секунд</div>
    <div className="metric-grid dashboard-account-stats">{cards.map(([label, value, detail]) => <article key={String(label)}><span>{label}</span><strong>{value}</strong><small>{detail}</small></article>)}</div>
    <div className="dashboard-grid">
      <Panel title="Профили сейчас"><dl className="stats"><div><dt>Активные</dt><dd>{data.profiles.active}</dd></div><div><dt>Запущенные</dt><dd>{data.profiles.active_leases}</dd></div><div><dt>Требуют восстановления</dt><dd>{data.profiles.recovery_required}</dd></div></dl></Panel>
      <Panel title="Требует внимания"><div className="attention"><Badge tone={data.devices.pending ? "warn" : "good"}>{data.devices.pending}</Badge><span>устройств ожидают подтверждения</span></div><div className="attention"><Badge tone={data.profiles.recovery_required ? "bad" : "good"}>{data.profiles.recovery_required}</Badge><span>профилей требуют восстановления</span></div><div className="attention"><Badge tone={data.profiles.stale_leases ? "bad" : "good"}>{data.profiles.stale_leases}</Badge><span>устаревших запусков требуют проверки</span></div><div className="attention"><Badge tone={data.recent_failures ? "warn" : "good"}>{data.recent_failures}</Badge><span>ошибок в журнале за 24 часа</span></div></Panel>
    </div>
  </>;
}
function PageHead({ title, subtitle, action }: { title: string; subtitle: string; action?: ReactNode }) { return <div className="page-head"><div><p className="eyebrow">Server GShard</p><h1>{title}</h1><p>{subtitle}</p></div>{action}</div>; }
function Loader() { return <div className="loader"><i />Загрузка…</div>; }

function Accounts() {
  const [search, setSearch] = useUrlState("accounts.search", "");
  const query = useDebouncedValue(search);
  const [create, setCreate] = useState(false);
  const [created, setCreated] = useState<any>(null);
  const [selected, setSelected] = useState<any>(null);
  const path = `/api/v1/admin/accounts/?search=${encodeURIComponent(query)}`;
  const { data, error, load, pagination } = usePaged<Page<any>>(path, "accounts");
  const updateStatus = async (row: any, status: string) => { if (!confirm(`${status === "BLOCKED" ? "Заблокировать" : "Разблокировать"} ${row.display_name}?`)) return; await patch(`/api/v1/admin/accounts/${row.id}`, { status }); load(); };
  return <><PageHead title="Учётные записи" subtitle="Пользователи, правила подключения, лимиты устройств и права доступа." action={<button className="primary compact" onClick={() => setCreate(true)}>Добавить пользователя</button>} /><div className="toolbar"><input placeholder="Поиск пользователей…" value={search} onChange={(event) => setSearch(event.target.value)} /><button className="secondary compact" onClick={load}>Обновить</button></div>{error && <ErrorBox error={error} />}{!data ? <Loader /> : <Panel title={`${data.items.length} учётных записей на странице`}><DataTable rows={data.items} columns={[
    { key: "display_name", label: "Учётная запись", render: (row) => <div className="primary-cell"><strong>{row.display_name}</strong><small>{row.username} · {row.email}</small></div> },
    { key: "role", label: "Роль", render: (row) => <Badge>{row.role}</Badge> },
    { key: "status", label: "Статус", render: (row) => <Badge>{row.status}</Badge> },
    { key: "device_count", label: "ПК / лимит", render: (row) => `${row.device_count} / ${row.max_devices}` },
    { key: "active_session_count", label: "Сеансы" },
    { key: "last_activity_at", label: "Последняя активность", render: (row) => fmtDate(row.last_activity_at) },
  ]} actions={(row) => <><button onClick={() => setSelected(row)}>Подробнее</button><button onClick={() => updateStatus(row, row.status === "BLOCKED" ? "ACTIVE" : "BLOCKED")}>{row.status === "BLOCKED" ? "Разблокировать" : "Заблокировать"}</button></>} />{pagination}</Panel>}
    {create && <AccountModal onClose={() => setCreate(false)} onCreated={(value) => { setCreate(false); setCreated(value); load(); }} />}
    {created && <SecretModal title={created.secret_kind === "activation" ? "Код активации создан" : "Временный пароль создан"} secret={created.temporary_password} label={`${created.secret_kind === "activation" ? "Код активации устройства" : "Временный пароль"} для ${created.username}`} onClose={() => setCreated(null)} />}
    {selected && <AccountAccessModal account={selected} onClose={() => setSelected(null)} onChanged={load} onSecret={setCreated} />}
  </>;
}

function AccountModal({ onClose, onCreated }: { onClose: () => void; onCreated: (value: any) => void }) {
  const [form, setForm] = useState({ username: "", email: "", display_name: "", role: "USER", device_policy: "APPROVAL_REQUIRED", max_devices: 3 });
  const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => { event.preventDefault(); setBusy(true); try { onCreated(await post("/api/v1/admin/accounts/", { ...form, temporary_password: null })); } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); } finally { setBusy(false); } };
  return <Modal title="Создание пользователя" onClose={onClose}><form className="modal-form" onSubmit={submit}><Field label="Имя пользователя" value={form.username} onChange={(value) => setForm({ ...form, username: value })} /><Field label="Электронная почта" value={form.email} onChange={(value) => setForm({ ...form, email: value })} /><Field label="Отображаемое имя" value={form.display_name} onChange={(value) => setForm({ ...form, display_name: value })} /><label className="field"><span>Роль</span><select value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })}><option value="USER">Пользователь</option><option value="ADMIN">Администратор</option><option value="SUPERADMIN">Суперадминистратор</option></select></label><label className="field"><span>Правило подключения устройств</span><select value={form.device_policy} onChange={(event) => setForm({ ...form, device_policy: event.target.value })}><option value="APPROVAL_REQUIRED">С подтверждением</option><option value="AUTO">Автоматически</option><option value="ACTIVATION_CODE">По коду активации</option></select></label><label className="field"><span>Максимум устройств</span><input type="number" min="1" max="100" value={form.max_devices} onChange={(event) => setForm({ ...form, max_devices: Number(event.target.value) })} /></label>{error && <ErrorBox error={error} />}<div className="modal-actions"><button type="button" className="secondary" onClick={(event) => requestModalClose(event.currentTarget, onClose)}>Отмена</button><button className="primary" disabled={busy}>{busy ? "Создание…" : "Создать"}</button></div></form></Modal>;
}

function AccountAccessModal({ account, onClose, onChanged, onSecret }: { account: any; onClose: () => void; onChanged: () => void; onSecret: (value: any) => void }) {
  const [profiles, setProfiles] = useState<any[]>([]); const [groups, setGroups] = useState<any[]>([]); const [grants, setGrants] = useState<any>({ direct: [], groups: [] });
  const [form, setForm] = useState({ display_name: account.display_name, email: account.email, role: account.role, device_policy: account.device_policy, max_devices: account.max_devices });
  const [busy, setBusy] = useState(true); const [error, setError] = useState(""); const [search, setSearch] = useState("");
  const load = useCallback(async () => { setBusy(true); try { const [profileRows, groupRows, current] = await Promise.all([fetchAllPages<any>("/api/v1/admin/profiles/", "profiles"), api<any[]>("/api/v1/admin/profile-groups/"), api<any>(`/api/v1/admin/accounts/${account.id}/grants`)]); setProfiles(profileRows); setGroups(groupRows.filter((row) => !row.archived_at)); setGrants(current); setError(""); } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); } finally { setBusy(false); } }, [account.id]);
  useEffect(() => { load(); }, [load]);
  const toggle = (kind: "direct" | "groups", idKey: "profile_id" | "group_id", id: string) => setGrants((value: any) => { const rows = [...value[kind]]; const index = rows.findIndex((row: any) => row[idKey] === id && row.permission === "RUN"); if (index >= 0) rows.splice(index, 1); else rows.push({ [idKey]: id, permission: "RUN" }); return { ...value, [kind]: rows }; });
  const inheritedRun = (kind: "direct" | "groups", idKey: "profile_id" | "group_id", id: string) => form.role !== "USER" || grants[kind].some((row: any) => row[idKey] === id && row.permission === "ADMIN");
  const hasRun = (kind: "direct" | "groups", idKey: "profile_id" | "group_id", id: string) => inheritedRun(kind, idKey, id) || grants[kind].some((row: any) => row[idKey] === id && row.permission === "RUN");
  const permissions = (kind: "direct" | "groups", idKey: "profile_id" | "group_id", id: string) => grants[kind].filter((row: any) => row[idKey] === id).map((row: any) => row.permission).join(", ");
  const save = async () => { setBusy(true); setError(""); try { await put(`/api/v1/admin/accounts/${account.id}`, { ...form, expected_updated_at: account.updated_at, grants: { direct: grants.direct.map(({ profile_id, permission }: any) => ({ profile_id, permission })), groups: grants.groups.map(({ group_id, permission }: any) => ({ group_id, permission })) } }); onChanged(); onClose(); } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); } finally { setBusy(false); } };
  const reset = async (resetTotp: boolean) => { if (!confirm(`Сбросить ${resetTotp ? "пароль и TOTP" : "пароль"} пользователя ${account.display_name}? Все остальные сеансы будут завершены.`)) return; try { const value = await post<any>(`/api/v1/admin/accounts/${account.id}/reset-password`, { temporary_password: null, reset_totp: resetTotp }); onSecret(value); onChanged(); onClose(); } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); } };
  const activation = async () => { try { const value = await post<any>(`/api/v1/admin/accounts/${account.id}/activation-codes`, { expires_in_hours: 24 }); onSecret({ username: account.username, temporary_password: value.code, secret_kind: "activation" }); onClose(); } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); } };
  const setStatus = async (status: "ACTIVE" | "BLOCKED" | "DISABLED") => { if (!confirm(`${status === "ACTIVE" ? "Активировать" : status === "BLOCKED" ? "Заблокировать" : "Отключить"} пользователя ${account.display_name}?${status === "ACTIVE" ? "" : " Все сеансы будут завершены, а запущенные профили перейдут в восстановление."}`)) return; try { await patch(`/api/v1/admin/accounts/${account.id}`, { status }); onChanged(); onClose(); } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); } };
  const revokeSessions = async () => { if (!confirm(`Завершить все сеансы пользователя ${account.display_name}? Запущенные профили перейдут в восстановление.`)) return; try { await post(`/api/v1/admin/accounts/${account.id}/revoke-sessions`, { reason: "Завершено из панели администратора" }); onChanged(); onClose(); } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); } };
  const archive = async () => { if (!confirm(`Архивировать пользователя ${account.display_name}? Сеансы и активные аренды будут отозваны. Действие нельзя отменить в панели.`)) return; try { await post(`/api/v1/admin/accounts/${account.id}/archive`, {}); onChanged(); onClose(); } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); } };
  const visibleProfiles = profiles.filter((row) => row.name.toLowerCase().includes(search.toLowerCase()));
  return <Modal title={`Пользователь · ${account.display_name}`} onClose={onClose}>{busy && !profiles.length ? <Loader /> : <div className="account-editor"><div className="editor-grid"><Field label="Отображаемое имя" value={form.display_name} onChange={(value) => setForm({ ...form, display_name: value })} /><Field label="Электронная почта" value={form.email} onChange={(value) => setForm({ ...form, email: value })} /><label className="field"><span>Роль</span><select value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })}><option value="USER">Пользователь</option><option value="ADMIN">Администратор</option><option value="SUPERADMIN">Суперадминистратор</option></select></label><label className="field"><span>Правило подключения устройств</span><select value={form.device_policy} onChange={(event) => setForm({ ...form, device_policy: event.target.value })}><option value="AUTO">Автоматически</option><option value="APPROVAL_REQUIRED">С подтверждением</option><option value="ACTIVATION_CODE">По коду активации</option></select></label><label className="field"><span>Максимум устройств</span><input type="number" min="1" max="100" value={form.max_devices} onChange={(event) => setForm({ ...form, max_devices: Number(event.target.value) })} /></label></div><div className="editor-section"><header><div><strong>Доступ к профилям</strong><small>Здесь меняется прямое право запуска. Если запуск входит в роль администратора или право ADMIN, флажок заблокирован. Расширенные разрешения сохраняются.</small></div><input placeholder="Поиск профилей…" value={search} onChange={(event) => setSearch(event.target.value)} /></header><div className="grant-list">{visibleProfiles.map((row) => <label key={row.id}><input type="checkbox" checked={hasRun("direct", "profile_id", row.id)} disabled={busy || inheritedRun("direct", "profile_id", row.id)} title={inheritedRun("direct", "profile_id", row.id) ? "Запуск разрешён ролью администратора или правом ADMIN" : "Прямое право запуска"} onChange={() => toggle("direct", "profile_id", row.id)} /><span><strong>{row.name}</strong><small>{row.group_name || "Без группы"}{permissions("direct", "profile_id", row.id) ? ` · ${permissions("direct", "profile_id", row.id)}` : ""}</small></span></label>)}</div></div><div className="editor-section"><header><div><strong>Доступ к группам</strong><small>Право запуска применяется ко всем профилям группы.</small></div></header><div className="grant-list compact-list">{groups.map((row) => <label key={row.id}><input type="checkbox" checked={hasRun("groups", "group_id", row.id)} disabled={busy || inheritedRun("groups", "group_id", row.id)} title={inheritedRun("groups", "group_id", row.id) ? "Запуск разрешён ролью администратора или правом ADMIN" : "Прямое право запуска"} onChange={() => toggle("groups", "group_id", row.id)} /><span><strong>{row.name}</strong><small>{row.profile_count} профилей{permissions("groups", "group_id", row.id) ? ` · ${permissions("groups", "group_id", row.id)}` : ""}</small></span></label>)}</div></div>{error && <ErrorBox error={error} />}<div className="danger-zone"><strong>Действия безопасности</strong><div><button className="danger" onClick={() => reset(false)}>Сбросить пароль</button><button className="danger" onClick={() => reset(true)}>Сбросить пароль и TOTP</button><button onClick={activation}>Создать код активации</button><button onClick={revokeSessions}>Завершить сеансы</button>{account.status !== "ACTIVE" && <button className="approve" onClick={() => setStatus("ACTIVE")}>Активировать</button>}{account.status !== "BLOCKED" && <button className="danger" onClick={() => setStatus("BLOCKED")}>Заблокировать</button>}{account.status !== "DISABLED" && <button className="danger" onClick={() => setStatus("DISABLED")}>Отключить</button>}<button className="danger" onClick={archive}>Архивировать пользователя</button></div></div><div className="modal-actions"><button className="secondary" onClick={(event) => requestModalClose(event.currentTarget, onClose)}>Отмена</button><button className="primary" disabled={busy} onClick={save}>{busy ? "Сохранение…" : "Сохранить пользователя"}</button></div></div>}</Modal>;
}

function Devices() {
  const [status, setStatus] = useUrlState("devices.status", "CURRENT");
  const [actionError, setActionError] = useState("");
  const { data, error, load, pagination } = usePaged<Page<any>>(
    `/api/v1/admin/devices/?status=${status}`, "devices",
  );
  const decide = async (row: any, action: string) => {
    const dangerous = ["DENY", "BLOCK", "REVOKE"].includes(action);
    if (
      dangerous &&
      !confirm(
        `${valueLabels[action] ?? action}: устройство «${row.display_name}»? Активные сеансы будут завершены, а запущенные профили перейдут в восстановление.`,
      )
    )
      return;
    setActionError("");
    try {
      await post(`/api/v1/admin/devices/${row.id}/decision`, {
        action,
        reason: `Панель администратора: ${action.toLowerCase()}`,
        best_effort_wipe: false,
      });
      await load();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : String(reason));
    }
  };
  const rename = async (row: any) => {
    const display_name = prompt(
      "Название устройства",
      row.display_name,
    )?.trim();
    if (!display_name || display_name === row.display_name) return;
    setActionError("");
    try {
      await patch(`/api/v1/admin/devices/${row.id}`, { display_name });
      await load();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : String(reason));
    }
  };
  const revokeSessions = async (row: any) => {
    if (
      !confirm(
        `Завершить все сеансы на устройстве «${row.display_name}»? Запущенные профили перейдут в восстановление, решение о доверии сохранится.`,
      )
    )
      return;
    setActionError("");
    try {
      await post(`/api/v1/admin/devices/${row.id}/revoke-sessions`, {
        reason: "Завершено из панели администратора",
      });
      await load();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : String(reason));
    }
  };
  return (
    <>
      <PageHead
        title="Устройства"
        subtitle="Повторные входы и обновления приложения сохраняют устройство. Браузеры не занимают места в лимите ПК; их доступ и сеансы показаны отдельно."
        action={
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="CURRENT">Текущие устройства</option>
            <option value="">Вся история</option>
            <option value="PENDING">Ожидает</option>
            <option value="TRUSTED">Доверенное</option>
            <option value="BLOCKED">Заблокировано</option>
            <option value="REVOKED">Доступ отозван</option>
            <option value="DENIED">Отклонено</option>
          </select>
        }
      />
      {(error || actionError) && (
        <ErrorBox
          error={error || actionError}
          onClose={actionError ? () => setActionError("") : undefined}
        />
      )}
      {!data ? (
        <Loader />
      ) : (
        <Panel title={`${data.items.length} устройств на странице`}>
          <DataTable
            rows={data.items}
            columns={[
              {
                key: "display_name",
                label: "Устройство",
                render: (row) => (
                  <div className="primary-cell">
                    <strong>{row.display_name}</strong>
                    <small>
                      {row.account_name} · {row.username}
                    </small>
                  </div>
                ),
              },
              {
                key: "status",
                label: "Доверие",
                render: (row) => <Badge>{row.status}</Badge>,
              },
              {
                key: "platform",
                label: "Платформа",
                render: (row) => (
                  <div className="primary-cell">
                    <strong>
                      {row.platform === "WEB" ? "Браузер · вне лимита ПК" : `${row.platform}/${row.arch || "неизвестно"}`}
                    </strong>
                    <small>
                      {row.os_version || "Неизвестная ОС"} · впервые:{" "}
                      {fmtDate(row.first_seen_at)}
                    </small>
                  </div>
                ),
              },
              {
                key: "client_version",
                label: "Клиент",
                render: (row) =>
                  `${row.client_version} · p${row.client_protocol_version}`,
              },
              { key: "active_session_count", label: "Сеансы" },
              { key: "active_lease_count", label: "Аренды" },
              {
                key: "last_seen_at",
                label: "Последняя связь",
                render: (row) => fmtDate(row.last_seen_at),
              },
            ]}
            actions={(row) => (
              <>
                <button onClick={() => rename(row)}>Переименовать</button>
                {row.active_session_count > 0 && (
                  <button onClick={() => revokeSessions(row)}>
                    Завершить сеансы
                  </button>
                )}
                {row.status === "PENDING" ? (
                  <>
                    <button
                      className="approve"
                      onClick={() => decide(row, "APPROVE")}
                    >
                      Одобрить
                    </button>
                    <button
                      className="danger"
                      onClick={() => decide(row, "DENY")}
                    >
                      Отклонить
                    </button>
                  </>
                ) : row.status === "TRUSTED" ? (
                  <>
                    <button
                      className="danger"
                      onClick={() => decide(row, "BLOCK")}
                    >
                      Заблокировать
                    </button>
                    <button
                      className="danger"
                      onClick={() => decide(row, "REVOKE")}
                    >
                      Закрыть доступ
                    </button>
                  </>
                ) : null}
              </>
            )}
          />
        {pagination}</Panel>
      )}
    </>
  );
}

function Profiles({ me }: { me: Me }) {
  const [search, setSearch] = useUrlState("profiles.search", ""); const [create, setCreate] = useState(false); const [selected, setSelected] = useState<any>(null); const [actionError, setActionError] = useState("");
  const query = useDebouncedValue(search);
  const { data, error, load, pagination } = usePaged<Page<any>>(`/api/v1/admin/profiles/?search=${encodeURIComponent(query)}`, "profiles");
  useEffect(() => {
    const timer = window.setInterval(load, 5000);
    const refreshOnFocus = () => { void load(); };
    window.addEventListener("focus", refreshOnFocus);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", refreshOnFocus); };
  }, [load]);
  const archive = async (row: any) => { if (!confirm(`Архивировать профиль «${row.name}»? Неизменяемая история сохранится, но профиль исчезнет из каталогов пользователей.`)) return; setActionError(""); try { await post(`/api/v1/admin/profiles/${row.id}/archive`, {}); await load(); } catch (reason) { setActionError(reason instanceof Error ? reason.message : String(reason)); } };
  return <><PageHead title="Профили" subtitle="Профили браузера, группы и состояние синхронизации." action={<button className="primary compact" onClick={() => setCreate(true)}>Добавить профиль</button>} /><div className="toolbar"><input placeholder="Поиск профилей…" value={search} onChange={(event) => setSearch(event.target.value)} /><button className="secondary compact" onClick={load}>Обновить</button></div>{(error || actionError) && <ErrorBox error={error || actionError} onClose={actionError ? () => setActionError("") : undefined} />}{!data ? <Loader /> : <Panel title={`${data.items.length} профилей на странице`}><DataTable rows={data.items} columns={[
    { key: "name", label: "Профиль", render: (row) => <div className="primary-cell"><strong>{row.name}</strong><small>{row.description || row.id}</small></div> },
    { key: "group_name", label: "Группа" },
    { key: "status", label: "Статус", render: (row) => <Badge>{row.status}</Badge> },
    { key: "current_revision_number", label: "Версия данных", render: (row) => row.current_revision_number ? `r${row.current_revision_number}` : "Начальная" },
    { key: "active_lease_id", label: "Использование", render: (row) => row.active_lease_id ? <Badge tone="warn">Используется</Badge> : "—" },
    { key: "updated_at", label: "Изменён", render: (row) => fmtDate(row.updated_at) },
  ]} actions={(row) => <><button onClick={() => setSelected(row)}>Подробнее</button><button className="danger" disabled={Boolean(row.active_lease_id)} onClick={() => archive(row)}>Архивировать</button></>} />{pagination}</Panel>}{create && <ProfileModal me={me} onClose={() => setCreate(false)} onSaved={() => { setCreate(false); load(); }} />}{selected && <ProfileModal me={me} profile={selected} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); load(); }} />}</>;
}

function ProfileModal({
  me,
  profile,
  onClose,
  onSaved,
}: {
  me: Me;
  profile?: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<{
    external_id: string;
    name: string;
    icon_key: string;
    description: string;
    group_id: string;
    proxy_id: string;
    tags: string;
    status: string;
    platform: JsonObject;
    fingerprint: JsonObject;
  }>({
    external_id: profile?.external_id ?? "",
    name: profile?.name ?? "",
    icon_key: profile?.icon_key ?? "DEFAULT",
    description: profile?.description ?? "",
    group_id: profile?.group_id ?? "",
    proxy_id: profile?.proxy_id ?? "",
    tags: (profile?.tags ?? []).join(", "),
    status: profile?.status ?? "ACTIVE",
    platform: profile?.platform_constraints ?? {},
    fingerprint:
      profile?.fingerprint_config ?? createDefaultFingerprintConfig(),
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [configurationSource, setConfigurationSource] = useState("");
  const presetInputRef = useRef<HTMLInputElement>(null);
  const [importProgress, setImportProgress] = useState("");
  const { data: groups, error: groupsError } = useLoad<any[]>(
    "/api/v1/admin/profile-groups/",
  );
  const { data: proxies, error: proxiesError } = useLoad<any[]>(
    "/api/v1/admin/proxies/",
  );
  const { data: templates, error: templatesError } = useLoad<any[]>(
    "/api/v1/admin/profile-templates/",
  );
  const { data: devicePresets, error: deviceError, load: loadDevices } = useLoad<DevicePreset[]>(
    "/api/v1/admin/fingerprint-presets/",
  );
  const { data: sourceProfiles, error: sourceProfilesError } = useAllPages<any>(
    "/api/v1/admin/profiles/",
    "профилей",
  );
  useEffect(() => {
    if (profile || !devicePresets?.length) return;
    const firstWindows = devicePresets.find((preset) => preset.platform === "Windows");
    if (!firstWindows) return;
    setForm((current) => current.fingerprint.webgl?.renderer
      ? current
      : { ...current, fingerprint: applyDevicePreset(firstWindows) });
  }, [devicePresets, profile]);
  const applyConfiguration = () => {
    const [kind, id] = configurationSource.split(":", 2);
    const source =
      kind === "template"
        ? templates?.find((row) => row.id === id)
        : sourceProfiles?.find((row) => row.id === id);
    if (!source) return;
    setForm({
      ...form,
      platform: source.platform_constraints ?? {},
      fingerprint:
        source.fingerprint_config ?? createDefaultFingerprintConfig(),
    });
  };
  const importDevices = async (files: FileList | null) => {
    if (!files?.length) return;
    setError("");
    setBusy(true);
    let imported = 0;
    try {
      if (files.length > 1000) throw new Error("Выберите не более 1000 JSON-файлов устройств.");
      for (const file of Array.from(files)) {
        if (file.size > 64 * 1024) throw new Error(`Файл ${file.name} больше 64 КБ.`);
        const payload = JSON.parse(await file.text()) as JsonObject;
        if (!payload || Array.isArray(payload) || typeof payload !== "object")
          throw new Error(`Файл ${file.name} не содержит объект отпечатка.`);
        delete payload.notes;
        delete payload._meta;
        const rawPlatform = String(payload?.navigator?.platform ?? "").toLowerCase();
        const platform = rawPlatform.includes("win") ? "Windows"
          : rawPlatform.includes("mac") ? "macOS"
          : rawPlatform.includes("linux") ? "Linux" : "";
        const id = file.name.replace(/\.json$/i, "");
        const label = String(payload?.name ?? id);
        const gpu = String(payload?.webgl?.renderer ?? "");
        if (!platform || !gpu || !/^[A-Za-z0-9._-]{1,128}$/.test(id))
          throw new Error(`В файле ${file.name} нет корректного ID, ОС или GPU.`);
        await post("/api/v1/admin/fingerprint-presets/", { id, label, platform, gpu, payload });
        setImportProgress(`Импорт устройств: ${++imported} / ${files.length}`);
      }
    } catch (reason) {
      setError(`Импортировано ${imported} из ${files.length}. ${reason instanceof Error ? reason.message : String(reason)}`);
    } finally {
      await loadDevices();
      setBusy(false);
      setImportProgress("");
    }
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const common = {
        name: form.name,
        icon_key: form.icon_key,
        description: form.description,
        group_id: form.group_id || null,
        proxy_id: form.proxy_id || null,
        tags: form.tags
          .split(/[,\n]/)
          .map((tag: string) => tag.trim())
          .filter(Boolean),
        platform_constraints: form.platform,
        fingerprint_config: {
          ...form.fingerprint,
          name: form.name.trim(),
          notes: form.description.trim(),
        },
      };
      if (profile)
        await patch(`/api/v1/admin/profiles/${profile.id}`, {
          ...common,
          clear_group: !form.group_id,
          clear_proxy: !form.proxy_id,
          ...(profile.status !== "RECOVERY_REQUIRED" &&
          form.status !== profile.status
            ? { status: form.status }
            : {}),
        });
      else
        await post("/api/v1/admin/profiles/", {
          ...common,
          external_id: form.external_id.trim() || null,
        });
      onSaved();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  };
  const loadError =
    groupsError || proxiesError || templatesError || sourceProfilesError || deviceError;
  return (
    <Modal
      title={
        profile ? `Профиль · ${profile.name}` : "Создание профиля Server GShard"
      }
      onClose={onClose}
      wide
    >
      <form className="modal-form profile-editor" onSubmit={submit}>
        <div className="editor-grid">
          <Field
            label="Название"
            value={form.name}
            onChange={(value) => setForm({ ...form, name: value })}
          />
          <label className="field">
            <span>Значок профиля</span>
            <select value={form.icon_key} onChange={(event) => setForm({ ...form, icon_key: event.target.value })}>
              <option value="DEFAULT">GShard</option>
              <option value="BYBIT">Bybit</option>
              <option value="HTX">HTX</option>
              <option value="MEXC">MEXC</option>
            </select>
          </label>
          {!profile && (
            <Field
              label="Внешний ID (необязательно)"
              value={form.external_id}
              onChange={(value) => setForm({ ...form, external_id: value })}
            />
          )}
          <label className="field">
            <span>Группа</span>
            <select
              value={form.group_id}
              onChange={(event) =>
                setForm({ ...form, group_id: event.target.value })
              }
            >
              <option value="">Без группы</option>
              {groups
                ?.filter((row) => !row.archived_at)
                .map((row) => (
                  <option value={row.id} key={row.id}>
                    {row.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="field">
            <span>Прокси</span>
            <select
              value={form.proxy_id}
              onChange={(event) =>
                setForm({ ...form, proxy_id: event.target.value })
              }
            >
              <option value="">Без прокси</option>
              {proxies
                ?.filter((row) => row.status !== "DISABLED")
                .map((row) => (
                  <option value={row.id} key={row.id}>
                    {row.name}
                  </option>
                ))}
            </select>
          </label>
          {profile && profile.status !== "RECOVERY_REQUIRED" && (
            <label className="field">
              <span>Статус</span>
              <select
                value={form.status}
                onChange={(event) =>
                  setForm({ ...form, status: event.target.value })
                }
              >
                <option value="ACTIVE">Активен</option>
                <option value="DISABLED">Отключён</option>
              </select>
            </label>
          )}
          <Field
            label="Метки (через запятую)"
            value={form.tags}
            onChange={(value) => setForm({ ...form, tags: value })}
          />
        </div>
        <label className="field">
          <span>Описание</span>
          <textarea
            value={form.description}
            onChange={(event) =>
              setForm({ ...form, description: event.target.value })
            }
            rows={3}
          />
        </label>
        {profile?.status === "RECOVERY_REQUIRED" && (
          <div className="alert info">
            Перед изменением статуса завершите восстановление профиля.
          </div>
        )}
        <div className="template-picker">
          <label className="field">
            <span>Готовая конфигурация отпечатка</span>
            <select
              value={configurationSource}
              onChange={(event) => setConfigurationSource(event.target.value)}
            >
              <option value="">Базовая автоматическая конфигурация</option>
              {Boolean(templates?.length) && (
                <optgroup label="Серверные шаблоны">
                  {templates?.map((row) => (
                    <option
                      value={`template:${row.id}`}
                      key={`template:${row.id}`}
                    >
                      {row.name}
                      {row.fingerprint_config?.webgl?.renderer
                        ? ` · ${row.fingerprint_config.webgl.renderer}`
                        : ""}
                    </option>
                  ))}
                </optgroup>
              )}
              {Boolean(
                sourceProfiles?.some(
                  (row) => row.id !== profile?.id && row.status !== "ARCHIVED",
                ),
              ) && (
                <optgroup label="Готовые профили">
                  {sourceProfiles
                    ?.filter(
                      (row) =>
                        row.id !== profile?.id && row.status !== "ARCHIVED",
                    )
                    .map((row) => (
                      <option
                        value={`profile:${row.id}`}
                        key={`profile:${row.id}`}
                      >
                        {row.name}
                        {row.fingerprint_config?.webgl?.renderer
                          ? ` · ${row.fingerprint_config.webgl.renderer}`
                          : ""}
                      </option>
                    ))}
                </optgroup>
              )}
            </select>
          </label>
          <button
            type="button"
            className="secondary compact"
            disabled={!configurationSource}
            onClick={applyConfiguration}
          >
            Использовать конфигурацию
          </button>
        </div>
        {me.role === "SUPERADMIN" && <div className="template-picker">
          <p className="form-note">GPU и устройства берутся из защищённой библиотеки сервера. Суперадминистратор может импортировать те же JSON-отпечатки, которые использует приложение.</p>
          <input ref={presetInputRef} type="file" accept=".json,application/json" multiple hidden onChange={(event) => { void importDevices(event.target.files); event.target.value = ""; }} />
          <button type="button" className="secondary compact" disabled={busy} onClick={() => presetInputRef.current?.click()}>Импортировать устройства</button>
          {importProgress && <span role="status">{importProgress}</span>}
        </div>}
        <ProfileConfigurationEditor
          platformConstraints={form.platform}
          fingerprintConfig={form.fingerprint}
          devicePresets={devicePresets ?? []}
          onPlatformChange={(platform) =>
            setForm((current) => ({ ...current, platform }))
          }
          onFingerprintChange={(fingerprint) =>
            setForm((current) => ({ ...current, fingerprint }))
          }
        />
        <p className="form-note">
          Для первичного переноса данных используйте Desktop → Настройки →
          Импорт готовых профилей GShard / ShardX. Веб-панель не может читать
          произвольные локальные каталоги.
        </p>
        {(error || loadError) && <ErrorBox error={error || loadError} />}
        <div className="modal-actions">
          <button type="button" className="secondary" onClick={(event) => requestModalClose(event.currentTarget, onClose)}>
            Отмена
          </button>
          <button className="primary" disabled={busy || Boolean(loadError)}>
            {busy
              ? "Сохранение…"
              : profile
                ? "Сохранить профиль"
                : "Создать профиль"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Groups() {
  const { data, error, load } = useLoad<any[]>("/api/v1/admin/profile-groups/", true); const [create, setCreate] = useState(false); const [selected, setSelected] = useState<any>(null); const [actionError, setActionError] = useState("");
  const archive = async (row: any) => { if (!confirm(`Архивировать группу «${row.name}»? Профилей без группы станет: ${row.profile_count}. Выданные группе права перестанут действовать.`)) return; setActionError(""); try { await post(`/api/v1/admin/profile-groups/${row.id}/archive`, {}); await load(); } catch (reason) { setActionError(reason instanceof Error ? reason.message : String(reason)); } };
  return <><PageHead title="Группы профилей" subtitle="Группировка профилей и назначение прав доступа." action={<button className="primary compact" onClick={() => setCreate(true)}>Добавить группу</button>} />{(error || actionError) && <ErrorBox error={error || actionError} onClose={actionError ? () => setActionError("") : undefined} />}{!data ? <Loader /> : <Panel title={`${data.filter((row) => !row.archived_at).length} активных групп`}><DataTable rows={data} columns={[{ key: "name", label: "Группа", render: (row) => <div className="primary-cell"><strong>{row.name}</strong><small>{row.description}</small></div> }, { key: "profile_count", label: "Профили" }, { key: "grant_count", label: "Выданные доступы" }, { key: "updated_at", label: "Изменён", render: (row) => fmtDate(row.updated_at) }, { key: "archived_at", label: "Состояние", render: (row) => <Badge tone={row.archived_at ? "bad" : "good"}>{row.archived_at ? "В архиве" : "Активен"}</Badge> }]} actions={(row) => !row.archived_at ? <><button onClick={() => setSelected(row)}>Изменить</button><button className="danger" onClick={() => archive(row)}>Архивировать</button></> : null} /></Panel>}{create && <GroupModal onClose={() => setCreate(false)} onSaved={() => { setCreate(false); load(); }} />}{selected && <GroupModal group={selected} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); load(); }} />}</>;
}

function GroupModal({ group, onClose, onSaved }: { group?: any; onClose: () => void; onSaved: () => void }) { const [name, setName] = useState(group?.name ?? ""); const [description, setDescription] = useState(group?.description ?? ""); const [error, setError] = useState(""); const [busy, setBusy] = useState(false); const submit = async (event: FormEvent) => { event.preventDefault(); setBusy(true); setError(""); try { await post(group ? `/api/v1/admin/profile-groups/${group.id}` : "/api/v1/admin/profile-groups/", { name, description }); onSaved(); } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); } finally { setBusy(false); } }; return <Modal title={group ? `Группа · ${group.name}` : "Создание группы профилей"} onClose={onClose}><form className="modal-form" onSubmit={submit}><Field label="Название" value={name} onChange={setName} /><label className="field"><span>Описание</span><textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={4} /></label>{error && <ErrorBox error={error} />}<div className="modal-actions"><button type="button" className="secondary" onClick={(event) => requestModalClose(event.currentTarget, onClose)}>Отмена</button><button className="primary" disabled={busy}>{busy ? "Сохранение…" : group ? "Сохранить группу" : "Создать группу"}</button></div></form></Modal>; }

function Revisions() {
  const { data, error, load, pagination } = usePaged<Page<any>>("/api/v1/admin/revisions/", "revisions"); const [actionError, setActionError] = useState(""); const restore = async (row: any) => { if (!confirm(`Восстановить профиль «${row.profile_name}» до ревизии r${row.revision_number}? Профиль не должен быть запущен.`)) return; setActionError(""); try { await post(`/api/v1/admin/revisions/${row.id}/restore`, { idempotency_key: crypto.randomUUID(), notes: "Восстановлено из панели администратора" }); await load(); } catch (reason) { setActionError(reason instanceof Error ? reason.message : String(reason)); } };
  return <><PageHead title="Неизменяемые ревизии" subtitle="История версий, контроль целостности и явное восстановление." action={<button className="secondary compact" onClick={load}>Обновить</button>} />{(error || actionError) && <ErrorBox error={error || actionError} onClose={actionError ? () => setActionError("") : undefined} />}{!data ? <Loader /> : <Panel title={`${data.items.length} ревизий на странице`}><DataTable rows={data.items} columns={[{ key: "profile_name", label: "Профиль", render: (row) => <div className="primary-cell"><strong>{row.profile_name}</strong><small>r{row.revision_number} · {valueLabel(row.source)}{row.is_current ? " · текущая" : ""}</small></div> }, { key: "status", label: "Статус", render: (row) => <Badge>{row.status}</Badge> }, { key: "size_bytes", label: "Данные", render: (row) => `${fmtBytes(row.size_bytes)} · ${row.file_count} файлов` }, { key: "engine_version", label: "Движок", render: (row) => row.engine_version || "—" }, { key: "created_by_name", label: "Создал" }, { key: "created_at", label: "Создана", render: (row) => fmtDate(row.created_at) }]} actions={(row) => !row.is_current && row.status !== "QUARANTINED" && <button onClick={() => restore(row)}>{row.status === "RECOVERY" ? "Принять и восстановить" : "Восстановить"}</button>} />{pagination}</Panel>}</>;
}

function Leases() {
  const { data, error, load } = useLoad<any[]>("/api/v1/admin/leases", true); const [actionError, setActionError] = useState(""); const [busyLeaseId, setBusyLeaseId] = useState<string | null>(null); const resolve = async (row: any, action: string) => { if (busyLeaseId) return; const message = action === "FORCE_RECOVERY" ? "Остановить профиль и сохранить устройство как источник восстановления?" : "Использовать текущую серверную ревизию и отклонить последующую фиксацию этой аренды? Несинхронизированные данные устройства могут быть потеряны."; if (!confirm(`${row.profile_name}: ${message}`)) return; setBusyLeaseId(row.id); setActionError(""); try { await post(`/api/v1/admin/leases/${row.id}/resolve`, { action, notes: `Решено из панели администратора: ${action}` }); await load(); } catch (reason) { setActionError(reason instanceof Error ? reason.message : String(reason)); } finally { setBusyLeaseId(null); } };
  const active = data?.filter((row) => row.can_force_recovery) ?? []; const attention = data?.filter((row) => row.pending_recovery) ?? [];
  return <><PageHead title="Запущенные профили" subtitle="Кто работает с профилем, когда была последняя связь и что требует внимания." action={<button className="secondary compact" onClick={load}>Обновить</button>} />{(error || actionError) && <ErrorBox error={error || actionError} onClose={actionError ? () => setActionError("") : undefined} />}{!data ? <Loader /> : <Panel title={`${active.length} активных · ${attention.length} требуют решения`}><DataTable rows={data} columns={[{ key: "profile_name", label: "Профиль", render: (row) => <div className="primary-cell"><strong>{row.profile_name}</strong></div> }, { key: "account_name", label: "Владелец", render: (row) => `${row.account_name} · ${row.device_name}` }, { key: "state", label: "Состояние", render: (row) => <Badge>{row.pending_recovery ? "Требуется решение" : row.state}</Badge> }, { key: "heartbeat_at", label: "Последняя связь", render: (row) => fmtDate(row.heartbeat_at) }, { key: "expires_at", label: "Истекает", render: (row) => fmtDate(row.expires_at) }]} actions={(row) => row.can_force_recovery || row.can_use_server_version ? <>{row.can_force_recovery && <button className="approve" disabled={busyLeaseId !== null} onClick={() => resolve(row, "FORCE_RECOVERY")}>{busyLeaseId === row.id ? "Обработка…" : "Сохранить для восстановления"}</button>}{row.can_use_server_version && <button className="danger" disabled={busyLeaseId !== null} onClick={() => resolve(row, "SERVER_AUTHORITATIVE")}>{busyLeaseId === row.id ? "Обработка…" : "Использовать серверную версию"}</button>}</> : null} /></Panel>}</>;
}

function Proxies() {
  const { data, error, load } = useLoad<any[]>("/api/v1/admin/proxies/", true); const [create, setCreate] = useState(false); const [selected, setSelected] = useState<any>(null);
  return <><PageHead title="Прокси" subtitle="Подключения к сети для профилей браузера. Пароли скрыты и хранятся зашифрованно." action={<button className="primary compact" onClick={() => setCreate(true)}>Добавить прокси</button>} />{error && <ErrorBox error={error} />}{!data ? <Loader /> : <Panel title={`${data.length} прокси`}><DataTable rows={data} columns={[{ key: "name", label: "Прокси", render: (row) => <div className="primary-cell"><strong>{row.name}</strong><small>{row.id}</small></div> }, { key: "kind", label: "Тип", render: (row) => <Badge>{row.kind}</Badge> }, { key: "host", label: "Адрес", render: (row) => `${row.host}:${row.port}` }, { key: "username", label: "Имя пользователя", render: (row) => row.username || "—" }, { key: "has_password", label: "Учётные данные", render: (row) => row.has_password ? "Сохранены зашифрованно" : "Нет" }, { key: "status", label: "Статус", render: (row) => <Badge>{row.status}</Badge> }]} actions={(row) => <button onClick={() => setSelected(row)}>Изменить</button>} /></Panel>}{create && <ProxyModal onClose={() => setCreate(false)} onSaved={() => { setCreate(false); load(); }} />}{selected && <ProxyModal proxy={selected} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); load(); }} />}</>;
}

function ProxyModal({ proxy, onClose, onSaved }: { proxy?: any; onClose: () => void; onSaved: () => void }) { const [form, setForm] = useState({ name: proxy?.name ?? "", kind: proxy?.kind ?? "SOCKS5", host: proxy?.host ?? "", port: proxy?.port ?? 1080, username: proxy?.username ?? "", password: "", status: proxy?.status ?? "ACTIVE", clear_password: false }); const [error, setError] = useState(""); const [busy, setBusy] = useState(false); const submit = async (event: FormEvent) => { event.preventDefault(); setBusy(true); setError(""); try { const body = { ...form, password: form.password || null, metadata: proxy?.metadata ?? {} }; await post(proxy ? `/api/v1/admin/proxies/${proxy.id}` : "/api/v1/admin/proxies/", body); onSaved(); } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); } finally { setBusy(false); } }; return <Modal title={proxy ? `Прокси · ${proxy.name}` : "Создание зашифрованного прокси"} onClose={onClose}><form className="modal-form" onSubmit={submit}><div className="editor-grid"><Field label="Название" value={form.name} onChange={(value) => setForm({ ...form, name: value })} /><label className="field"><span>Протокол</span><select value={form.kind} onChange={(event) => setForm({ ...form, kind: event.target.value })}><option>SOCKS5</option><option>HTTP</option><option>HTTPS</option></select></label><Field label="Хост" value={form.host} onChange={(value) => setForm({ ...form, host: value })} /><label className="field"><span>Порт</span><input type="number" min="1" max="65535" value={form.port} onChange={(event) => setForm({ ...form, port: Number(event.target.value) })} /></label><Field label="Имя пользователя" value={form.username} onChange={(value) => setForm({ ...form, username: value })} />{proxy && <label className="field"><span>Статус</span><select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}><option value="ACTIVE">Активен</option><option value="DISABLED">Отключён</option><option value="ERROR">Ошибка</option></select></label>}</div><Field label={proxy?.has_password ? "Новый пароль (оставьте пустым, чтобы сохранить текущий)" : "Пароль"} type="password" value={form.password} onChange={(value) => setForm({ ...form, password: value, clear_password: false })} />{proxy?.has_password && <label className="check"><input type="checkbox" checked={form.clear_password} onChange={(event) => setForm({ ...form, clear_password: event.target.checked, password: event.target.checked ? "" : form.password })} />Удалить сохранённый пароль</label>}<p className="form-note">Отключённый прокси не будет выдаваться при следующем запуске профиля. Уже запущенные браузеры не изменятся.</p>{error && <ErrorBox error={error} />}<div className="modal-actions"><button type="button" className="secondary" onClick={(event) => requestModalClose(event.currentTarget, onClose)}>Отмена</button><button className="primary" disabled={busy}>{busy ? "Сохранение…" : "Сохранить прокси"}</button></div></form></Modal>; }

function Health() {
  const { data: ready, error, failure, load } = useLoad<any>("/health/ready", true);
  const { data: live } = useLoad<any>("/health/live", true);
  const degraded = failure instanceof ApiError && failure.details && typeof failure.details === "object"
    ? failure.details as Record<string, unknown>
    : null;
  const state = ready ?? degraded;
  const cards: [string, unknown, string][] = state ? [
    ["API", live?.status ?? "unavailable", `Сервер ${live?.version || "—"}`],
    ["PostgreSQL", state.postgres, "Метаданные и транзакции"],
    ["Redis", state.redis, "Лимиты запросов и процессы входа"],
    ["Объектное хранилище", state.object_storage, "Неизменяемые данные профилей"],
  ] : [];
  return <><PageHead title="Состояние системы" subtitle="Проверка API и готовности зависимостей." action={<button className="secondary compact" onClick={load}>Запустить проверку</button>} />{error && <ErrorBox error={error} />}{!state && !error ? <Loader /> : cards.length > 0 && <div className="metric-grid">{cards.map(([name, componentState, detail]) => <article key={String(name)}><span>{name}</span><strong className={`health-state ${componentState === "unavailable" ? "bad" : ""}`}>{valueLabel(componentState as ReactNode)}</strong><small>{detail}</small></article>)}</div>}</>;
}

function Releases() {
  const { data, error, load } = useLoad<any[]>("/api/v1/admin/releases/", true); const [create, setCreate] = useState(false); const [selected, setSelected] = useState<any>(null); const [actionError, setActionError] = useState("");
  const publish = async (row: any) => { if (!confirm(`Опубликовать «${row.name}»? Запущенные профили не будут перезаписаны; публикация дождётся завершения всех затронутых аренд.`)) return; setActionError(""); try { await post(`/api/v1/admin/releases/${row.id}/publish`, {}); await load(); } catch (reason) { setActionError(reason instanceof Error ? reason.message : String(reason)); } };
  const rollback = async (row: any) => { if (!confirm(`Создать релиз отката на основе «${row.name}»?`)) return; setActionError(""); try { await post("/api/v1/admin/releases/rollback", { target_release_id: row.id, name: `Откат к ${row.name} · ${new Date().toISOString()}`, notes: "Создано из панели администратора" }); await load(); } catch (reason) { setActionError(reason instanceof Error ? reason.message : String(reason)); } };
  return <><PageHead title="Релизы профилей" subtitle="Создание, проверка, публикация и откат наборов ревизий." action={<button className="primary compact" onClick={() => setCreate(true)}>Создать релиз</button>} />{(error || actionError) && <ErrorBox error={error || actionError} onClose={actionError ? () => setActionError("") : undefined} />}{!data ? <Loader /> : <Panel title={`${data.length} релизов`}><DataTable rows={data} columns={[{ key: "name", label: "Релиз", render: (row) => <div className="primary-cell"><strong>{row.name}</strong><small>{row.notes || row.id}</small></div> }, { key: "status", label: "Статус", render: (row) => <Badge>{row.status}</Badge> }, { key: "target", label: "Назначение", render: (row) => valueLabel(row.target) }, { key: "item_count", label: "Профили" }, { key: "creator_name", label: "Создал" }, { key: "created_at", label: "Создана", render: (row) => fmtDate(row.created_at) }]} actions={(row) => <><button onClick={() => setSelected(row)}>Подробнее</button>{row.status === "DRAFT" && <button className="approve" onClick={() => publish(row)}>Опубликовать</button>}{row.status !== "DRAFT" && row.item_count > 0 && <button onClick={() => rollback(row)}>Откатить</button>}</>} /></Panel>}{create && <ReleaseModal onClose={() => setCreate(false)} onCreated={() => { setCreate(false); load(); }} />}{selected && <ReleaseDetailsModal release={selected} onClose={() => setSelected(null)} />}</>;
}

function ReleaseModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { data, error: loadError } = useAllPages<any>("/api/v1/admin/revisions/", "ревизий профилей"); const [name, setName] = useState(""); const [target, setTarget] = useState("EVERYONE"); const [notes, setNotes] = useState(""); const [selected, setSelected] = useState<Record<string, string>>({}); const [error, setError] = useState("");
  const grouped = useMemo(() => { const map = new Map<string, any[]>(); for (const revision of data ?? []) { if (!["DRAFT", "PUBLISHED", "SUPERSEDED"].includes(revision.status)) continue; const rows = map.get(revision.profile_id) ?? []; rows.push(revision); map.set(revision.profile_id, rows); } return [...map.values()].map((rows) => rows.sort((left, right) => right.revision_number - left.revision_number)); }, [data]);
  const submit = async (event: FormEvent) => { event.preventDefault(); const items = Object.entries(selected).filter(([, revision_id]) => revision_id).map(([profile_id, revision_id]) => ({ profile_id, revision_id })); if (!items.length) { setError("Выберите хотя бы одну ревизию профиля."); return; } try { await post("/api/v1/admin/releases/", { name, target, notes, items }); onCreated(); } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); } };
  return <Modal title="Создание релиза профилей" onClose={onClose}><form className="modal-form" onSubmit={submit}><Field label="Название / версия" value={name} onChange={setName} placeholder="2026.08.18-01" /><label className="field"><span>Назначение</span><select value={target} onChange={(event) => setTarget(event.target.value)}><option value="EVERYONE">Все пользователи</option></select></label><p className="form-note">Выборочная публикация пока отключена: все клиенты должны использовать единый указатель текущей ревизии.</p><label className="field"><span>Примечания к релизу</span><textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} /></label><div className="editor-section"><header><div><strong>Набор ревизий</strong><small>Выберите одну доступную ревизию для каждого нужного профиля. Можно использовать черновые, текущие и ранее опубликованные ревизии.</small></div></header><div className="release-picker">{grouped.map((rows) => <label key={rows[0].profile_id}><span><strong>{rows[0].profile_name}</strong><small>Доступно ревизий: {rows.length}</small></span><select value={selected[rows[0].profile_id] ?? ""} onChange={(event) => { setSelected({ ...selected, [rows[0].profile_id]: event.target.value }); setError(""); }}><option value="">Не включать</option>{rows.map((row) => <option value={row.id} key={row.id}>r{row.revision_number} · {valueLabel(row.status)}{row.is_current ? " · текущая" : ""} · {fmtBytes(row.size_bytes)}</option>)}</select></label>)}{data && grouped.length === 0 && <div className="empty">Нет ревизий, доступных для релиза. Сначала импортируйте, синхронизируйте или восстановите профиль.</div>}</div></div>{(error || loadError) && <ErrorBox error={error || loadError} />}<div className="modal-actions"><button type="button" className="secondary" onClick={(event) => requestModalClose(event.currentTarget, onClose)}>Отмена</button><button className="primary" disabled={!name.trim() || !Object.values(selected).some(Boolean) || Boolean(loadError)}>Создать черновик релиза</button></div></form></Modal>;
}

function ReleaseDetailsModal({ release, onClose }: { release: any; onClose: () => void }) { const { data, error } = useLoad<any>(`/api/v1/admin/releases/${release.id}`); return <Modal title={release.name} onClose={onClose}>{error && <ErrorBox error={error} />}{!data ? <Loader /> : <div className="release-details"><div className="release-summary"><Badge>{data.release.status}</Badge><span>{valueLabel(data.release.target)}</span><span>{fmtDate(data.release.published_at || data.release.created_at)}</span></div><p>{data.release.notes || "Нет примечаний к релизу."}</p><div className="grant-list">{data.items.map((row: any) => <div className="release-item" key={row.profile_id}><span><strong>{row.profile_name}</strong><small>r{row.revision_number} · файлов: {row.file_count} · {fmtBytes(row.size_bytes)}</small></span><Badge>{row.revision_status}</Badge></div>)}</div></div>}</Modal>; }

function Recovery() {
  const { data, error, load } = useLoad<any[]>("/api/v1/admin/recovery/", true);
  const [actionError, setActionError] = useState("");
  const resolve = async (row: any, action: string) => { const actionName: Record<string, string> = { PUBLISH_RECOVERY: "Опубликовать восстановление", CLONE: "Клонировать", USE_SERVER: "Оставить серверную версию", DISCARD: "Отбросить" }; if (!confirm(`${actionName[action] ?? action} для профиля «${row.profile_name}»?`)) return; setActionError(""); try { await post(`/api/v1/admin/recovery/${row.id}/resolve`, { action, notes: "Решено из панели администратора", clone_name: action === "CLONE" ? `${row.profile_name} — восстановление` : null }); await load(); } catch (reason) { setActionError(reason instanceof Error ? reason.message : String(reason)); } };
  return <><PageHead title="Очередь восстановления" subtitle="Обработка устаревших аренд и несинхронизированных снимков устройств." action={<button className="secondary compact" onClick={load}>Обновить</button>} />{(error || actionError) && <ErrorBox error={error || actionError} onClose={actionError ? () => setActionError("") : undefined} />}{!data ? <Loader /> : <Panel title={`${data.filter((row) => row.status === "PENDING").length} ожидают решения`}><DataTable rows={data} columns={[
    { key: "profile_name", label: "Профиль", render: (row) => <div className="primary-cell"><strong>{row.profile_name}</strong><small>{row.id}</small></div> },
    { key: "source_device_name", label: "Исходное устройство" },
    { key: "status", label: "Статус", render: (row) => <Badge>{row.status}</Badge> },
    { key: "recovery_revision_id", label: "Ревизия восстановления", render: (row) => row.recovery_revision_id ? "Загружена" : "Не загружена" },
    { key: "created_at", label: "Создана", render: (row) => fmtDate(row.created_at) },
  ]} actions={(row) => row.status === "PENDING" ? <><button className="approve" disabled={!row.recovery_revision_id} onClick={() => resolve(row, "PUBLISH_RECOVERY")}>Опубликовать</button><button disabled={!row.recovery_revision_id} onClick={() => resolve(row, "CLONE")}>Клонировать</button><button onClick={() => resolve(row, "USE_SERVER")}>Оставить серверную версию</button><button className="danger" onClick={() => resolve(row, "DISCARD")}>Отбросить</button></> : null} /></Panel>}</>;
}

function Sessions() {
  const [filter, setFilter] = useUrlState("sessions.filter", "ACTIVE");
  const { data, error, load, pagination } = usePaged<
    Page<any> & { active_count: number; history_count: number }
  >(`/api/v1/admin/sessions/?status=${filter}`, "sessions");
  const [actionError, setActionError] = useState("");
  const isActive = (row: any) =>
    !row.revoked_at && new Date(row.expires_at).getTime() > Date.now();
  const revoke = async (row: any) => {
    if (
      !confirm(
        `Завершить сеанс пользователя ${row.account_name} на устройстве ${row.device_name}?`,
      )
    )
      return;
    setActionError("");
    try {
      await post(`/api/v1/admin/sessions/${row.id}/revoke`, {
        reason: "Завершено из панели администратора",
      });
      await load();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : String(reason));
    }
  };
  return (
    <>
      <PageHead
        title="Сеансы"
        subtitle="Для одного веб-устройства действует только один сеанс; завершённые входы сохраняются как история."
        action={
          <div className="page-actions">
            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            >
              <option value="ACTIVE">Только активные</option>
              <option value="HISTORY">Завершённые</option>
              <option value="ALL">Все сеансы</option>
            </select>
            <button className="secondary compact" onClick={load}>
              Обновить
            </button>
          </div>
        }
      />
      {(error || actionError) && (
        <ErrorBox
          error={error || actionError}
          onClose={actionError ? () => setActionError("") : undefined}
        />
      )}
      {!data ? (
        <Loader />
      ) : (
        <Panel
          title={`${data.active_count} активных · ${data.history_count} завершённых`}
        >
          <DataTable
            rows={data.items}
            empty={
              filter === "ACTIVE"
                ? "Нет активных сеансов"
                : filter === "HISTORY"
                  ? "Нет завершённых сеансов"
                  : "Нет сеансов"
            }
            columns={[
              {
                key: "account_name",
                label: "Учётная запись",
                render: (row) => (
                  <div className="primary-cell">
                    <strong>{row.account_name}</strong>
                    <small>{row.username}</small>
                  </div>
                ),
              },
              {
                key: "device_name",
                label: "Устройство",
                render: (row) => `${row.device_name} · ${row.platform}`,
              },
              {
                key: "last_seen_at",
                label: "Последняя активность",
                render: (row) => fmtDate(row.last_seen_at),
              },
              {
                key: "expires_at",
                label: "Истекает",
                render: (row) => fmtDate(row.expires_at),
              },
              {
                key: "revoked_at",
                label: "Состояние",
                render: (row) => (
                  <Badge tone={isActive(row) ? "good" : "bad"}>
                    {row.revoked_at
                      ? "Отозван"
                      : isActive(row)
                        ? "Активен"
                        : "Истёк"}
                  </Badge>
                ),
              },
            ]}
            actions={(row) =>
              isActive(row) && (
                <button className="danger" onClick={() => revoke(row)}>
                  Завершить
                </button>
              )
            }
          />
        {pagination}</Panel>
      )}
    </>
  );
}

function Audit() {
  const [action, setAction] = useUrlState("audit.action", ""); const [result, setResult] = useUrlState("audit.result", "");
  const { data, error, load, pagination } = usePaged<Page<any>>(`/api/v1/admin/audit?action=${encodeURIComponent(action)}&result=${result}`, "audit");
  return <><PageHead title="Журнал аудита" subtitle="Неизменяемые события безопасности и администрирования." /><div className="toolbar"><input placeholder="Точное действие, например LOGIN_FAILED" value={action} onChange={(event) => setAction(event.target.value.toUpperCase())} /><select value={result} onChange={(event) => setResult(event.target.value)}><option value="">Все результаты</option><option value="SUCCESS">Успешно</option><option value="DENIED">Отклонено</option><option value="FAILED">Ошибка</option></select><button className="secondary compact" onClick={load}>Обновить</button></div>{error && <ErrorBox error={error} />}{!data ? <Loader /> : <Panel title={`${data.items.length} событий на странице`}><DataTable rows={data.items} columns={[
    { key: "occurred_at", label: "Время", render: (row) => fmtDate(row.occurred_at) },
    { key: "actor_name", label: "Инициатор", render: (row) => row.actor_name || row.actor_type },
    { key: "action", label: "Действие", render: (row) => <code>{row.action}</code> },
    { key: "object_type", label: "Объект", render: (row) => <div className="primary-cell"><strong>{row.object_type || "—"}</strong><small>{row.object_id || ""}</small></div> },
    { key: "result", label: "Результат", render: (row) => <Badge>{row.result}</Badge> },
  ]} />{pagination}</Panel>}</>;
}

function ConfigurationTemplates() {
  const { data, error, load } = useLoad<any[]>("/api/v1/admin/profile-templates/");
  const [selected, setSelected] = useState<any>(null); const [create, setCreate] = useState(false); const [actionError, setActionError] = useState("");
  const archive = async (row: any) => { if (!confirm(`Архивировать шаблон «${row.name}»? Уже созданные профили не изменятся.`)) return; setActionError(""); try { await post(`/api/v1/admin/profile-templates/${row.id}/archive`, {}); await load(); } catch (reason) { setActionError(reason instanceof Error ? reason.message : String(reason)); } };
  return <section className="templates-section"><div className="section-heading"><div><h2>Шаблоны конфигурации профилей</h2><p>Общие заготовки ограничений платформы и FingerprintConfig для быстрого создания профилей.</p></div><button className="primary compact" onClick={() => setCreate(true)}>Добавить шаблон</button></div>{(error || actionError) && <ErrorBox error={error || actionError} onClose={actionError ? () => setActionError("") : undefined} />}{!data ? <Loader /> : <Panel title={`${data.length} шаблонов`}><DataTable rows={data} empty="Шаблоны ещё не созданы" columns={[{ key: "name", label: "Шаблон", render: (row) => <div className="primary-cell"><strong>{row.name}</strong><small>{row.description || "Без описания"}</small></div> }, { key: "platform_constraints", label: "Платформы", render: (row) => row.platform_constraints.platforms?.join(", ") || "Без ограничений" }, { key: "fingerprint_config", label: "Отпечаток", render: (row) => Object.keys(row.fingerprint_config || {}).length ? "Настроен" : "Пустой" }, { key: "creator_name", label: "Автор" }, { key: "updated_at", label: "Изменён", render: (row) => fmtDate(row.updated_at) }]} actions={(row) => <><button onClick={() => setSelected(row)}>Изменить</button><button className="danger" onClick={() => archive(row)}>Архивировать</button></>} /></Panel>}{create && <ConfigurationTemplateModal onClose={() => setCreate(false)} onSaved={() => { setCreate(false); load(); }} />}{selected && <ConfigurationTemplateModal template={selected} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); load(); }} />}</section>;
}

function ConfigurationTemplateModal({
  template,
  onClose,
  onSaved,
}: {
  template?: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<{
    name: string;
    description: string;
    platform: JsonObject;
    fingerprint: JsonObject;
  }>({
    name: template?.name ?? "",
    description: template?.description ?? "",
    platform: template?.platform_constraints ?? {},
    fingerprint:
      template?.fingerprint_config ?? createDefaultFingerprintConfig(),
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sourceProfileId, setSourceProfileId] = useState("");
  const { data: sourceProfiles, error: sourceProfilesError } = useAllPages<any>(
    "/api/v1/admin/profiles/",
    "профилей",
  );
  const { data: devicePresets, error: deviceError } = useLoad<DevicePreset[]>(
    "/api/v1/admin/fingerprint-presets/",
  );
  const applySourceProfile = () => {
    const source = sourceProfiles?.find((row) => row.id === sourceProfileId);
    if (!source) return;
    setForm((current) => ({
      ...current,
      platform: source.platform_constraints ?? {},
      fingerprint:
        source.fingerprint_config ?? createDefaultFingerprintConfig(),
    }));
  };
  const windowsExample = () =>
    setForm({
      ...form,
      platform: {
        platforms: ["WINDOWS"],
        architectures: ["X86_64"],
        minimum_client_version: "0.1.10",
      },
    });
  const unrestricted = () => setForm({ ...form, platform: {} });
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = {
        name: form.name,
        description: form.description,
        platform_constraints: form.platform,
        fingerprint_config: form.fingerprint,
      };
      await post(
        template
          ? `/api/v1/admin/profile-templates/${template.id}`
          : "/api/v1/admin/profile-templates/",
        body,
      );
      onSaved();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={
        template ? `Шаблон · ${template.name}` : "Новый шаблон конфигурации"
      }
      onClose={onClose}
      wide
    >
      <form className="modal-form profile-editor" onSubmit={submit}>
        <div className="editor-grid">
          <Field
            label="Название"
            value={form.name}
            onChange={(value) => setForm({ ...form, name: value })}
          />
          <Field
            label="Описание"
            value={form.description}
            onChange={(value) => setForm({ ...form, description: value })}
          />
        </div>
        <div className="template-examples">
          <span>Быстрые примеры:</span>
          <button
            type="button"
            className="secondary compact"
            onClick={unrestricted}
          >
            Без ограничений
          </button>
          <button
            type="button"
            className="secondary compact"
            onClick={windowsExample}
          >
            Windows x64
          </button>
        </div>
        <div className="template-picker">
          <label className="field">
            <span>Взять готовый отпечаток из профиля</span>
            <select
              value={sourceProfileId}
              onChange={(event) => setSourceProfileId(event.target.value)}
            >
              <option value="">
                Выберите импортированный или настроенный профиль
              </option>
              {(sourceProfiles ?? [])
                .filter((row) => row.status !== "ARCHIVED")
                .map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
            </select>
          </label>
          <button
            type="button"
            className="secondary compact"
            disabled={!sourceProfileId}
            onClick={applySourceProfile}
          >
            Использовать конфигурацию
          </button>
        </div>
        <ProfileConfigurationEditor
          platformConstraints={form.platform}
          fingerprintConfig={form.fingerprint}
          devicePresets={devicePresets ?? []}
          onPlatformChange={(platform) =>
            setForm((current) => ({ ...current, platform }))
          }
          onFingerprintChange={(fingerprint) =>
            setForm((current) => ({ ...current, fingerprint }))
          }
        />
        {(error || sourceProfilesError || deviceError) && (
          <ErrorBox error={error || sourceProfilesError || deviceError} />
        )}
        <div className="modal-actions">
          <button type="button" className="secondary" onClick={(event) => requestModalClose(event.currentTarget, onClose)}>
            Отмена
          </button>
          <button
            className="primary"
            disabled={busy || Boolean(sourceProfilesError || deviceError)}
          >
            {busy ? "Сохранение…" : "Сохранить шаблон"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function System() {
  const { data, error, load } = useLoad<any[]>("/api/v1/admin/system");
  const [values, setValues] = useState<Record<string, number>>({}); const [notice, setNotice] = useState("");
  useEffect(() => { if (data) setValues(Object.fromEntries(data.map((row) => [row.key, Number(row.value)]))); }, [data]);
  const labels: Record<string, string> = { offline_grace_seconds: "Автономный резерв, секунд", lease_ttl_seconds: "Срок аренды, секунд", minimum_client_protocol_version: "Минимальная версия протокола клиента", sync_protocol_version: "Версия протокола синхронизации" };
  const save = async () => { try { await patch("/api/v1/admin/system", { values }); setNotice("Настройки сохранены."); load(); } catch (reason) { setNotice(reason instanceof Error ? reason.message : String(reason)); } };
  return <><PageHead title="Настройки" subtitle="Политики протокола, аренды, автономной работы и шаблоны профилей." action={<button className="primary compact" onClick={save}>Сохранить политики</button>} />{error && <ErrorBox error={error} />}{notice && <div className="alert info">{notice}</div>}{!data ? <Loader /> : <div className="settings-grid">{data.map((row) => <label className="setting" key={row.key}><span>{labels[row.key] ?? row.key}</span><input type="number" min="1" value={values[row.key] ?? ""} onChange={(event) => setValues({ ...values, [row.key]: Number(event.target.value) })} /><small>Последнее изменение: {fmtDate(row.updated_at)}</small></label>)}</div>}<ConfigurationTemplates /></>;
}

function SecretModal({ title, label, secret, onClose }: { title: string; label: string; secret: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false); const [error, setError] = useState("");
  return <Modal title={title} onClose={onClose} closeWarning={copied ? "" : "Секрет больше не будет показан. Вы сохранили его и хотите закрыть окно?"}><div className="secret"><p>{label}. Больше этот секрет показан не будет.</p><code>{secret}</code><button className="primary" onClick={async () => { try { await navigator.clipboard.writeText(secret); setCopied(true); setError(""); } catch { setError("Не удалось скопировать секрет. Выделите и сохраните его вручную."); } }}>{copied ? "Скопировано" : "Копировать секрет"}</button>{error && <ErrorBox error={error} />}</div></Modal>;
}

const baseNav: { section: string; icon: string; items: { id: PageId; label: string }[] }[] = [
  { section: "Обзор", icon: "⌁", items: [{ id: "dashboard", label: "Обзор" }] },
  { section: "P2P Monitor", icon: "◫", items: [{ id: "p2p", label: "P2P Monitor" }] },
  { section: "Профили", icon: "◆", items: [{ id: "profiles", label: "Все профили" }, { id: "leases", label: "Запущенные" }, { id: "groups", label: "Группы" }, { id: "proxies", label: "Прокси" }, { id: "revisions", label: "История" }, { id: "releases", label: "Наборы" }, { id: "recovery", label: "Восстановление" }] },
  { section: "Команда и доступ", icon: "◎", items: [{ id: "accounts", label: "Пользователи" }, { id: "devices", label: "Устройства" }, { id: "sessions", label: "Сеансы" }] },
  { section: "Журнал", icon: "≡", items: [{ id: "audit", label: "Журнал действий" }] },
  { section: "Система", icon: "⚙", items: [{ id: "health", label: "Состояние сервера" }, { id: "system", label: "Настройки" }] },
];

function Console({ me, onLogout, logoutError, loggingOut }: { me: Me; onLogout: () => void; logoutError: string; loggingOut: boolean }) {
  const nav = me.commercial ? baseNav.filter(group => group.section !== "Система").map(group => group.section === "Профили" ? { ...group, items: [...group.items, { id: "trash" as PageId, label: "Корзина" }] } : group) : baseNav;
  const [route, setRoute] = useUrlState("page", "dashboard");
  const page = nav.some((group) => group.items.some((item) => item.id === route)) ? route as PageId : "dashboard";
  const setPage = (value: PageId) => setRoute(value, true);
  const section = nav.find((group) => group.items.some((item) => item.id === page))!;
  const pageLabel = nav.flatMap((group) => group.items).find((item) => item.id === page)?.label ?? "Обзор";
  const content = useMemo(() => {
    if (page === "dashboard") return <Dashboard />;
    if (page === "p2p") return <P2PPage repricer={!me.commercial || me.commercial.features?.repricer === true} />;
    if (page === "accounts") return me.commercial ? <WorkspaceMembers me={me} /> : <Accounts />;
    if (page === "trash") return <WorkspaceTrash />;
    if (page === "devices") return <Devices />;
    if (page === "profiles") return <Profiles me={me} />;
    if (page === "groups") return <Groups />;
    if (page === "revisions") return <Revisions />;
    if (page === "leases") return <Leases />;
    if (page === "recovery") return <Recovery />;
    if (page === "sessions") return <Sessions />;
    if (page === "audit") return <Audit />;
    if (page === "system") return <System />;
    if (page === "proxies") return <Proxies />;
    if (page === "health") return <Health />;
    return <Releases />;
  }, [page, me]);
  return <div className="console"><aside className="sidebar"><Brand />
    <div className="sidebar-nav"><nav aria-label="Главные разделы"><span>Рабочее пространство</span>{nav.map((group) =>
      <button type="button" className={section === group ? "active" : ""} onClick={() => setPage(group.items[0].id)} key={group.section}
        aria-current={section === group ? "page" : undefined}><i aria-hidden="true">{group.icon}</i>{group.section}</button>)}</nav></div>
    <div className="sidebar-footnote">Единый контроль профилей<br />и P2P-операций</div>
    <div className="account-chip"><span>{me.display_name.slice(0, 2).toUpperCase()}</span><div><strong>{me.display_name}</strong><small>{valueLabels[me.role] ?? me.role}</small></div></div>
  </aside><main className="content"><header className="topbar"><div className="topbar-context">
    <label className="mobile-page-picker"><span>Раздел</span><select aria-label="Раздел панели" value={page} onChange={(event) => setPage(event.target.value as PageId)}>{nav.map((group) => <optgroup key={group.section} label={group.section}>{group.items.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</optgroup>)}</select></label>
    <strong className="current-page">{section.section}{section.items.length > 1 && <span> / {pageLabel}</span>}</strong><ServerConnection /></div>
    <div className="top-user"><span>{me.email}</span><button disabled={loggingOut} onClick={onLogout}>{loggingOut ? "Выход…" : "Выйти"}</button></div>
  </header><div className="page">{section.items.length > 1 && <SectionNav label={section.section} items={section.items} value={page} onChange={setPage} />}
    {me.commercial && <SubscriptionBanner me={me} />}{logoutError && <ErrorBox error={logoutError} />}{content}</div></main></div>;

}

function LegacyApp() {
  const [me, setMe] = useState<Me | null>(null); const [checking, setChecking] = useState(true);
  const [logoutError, setLogoutError] = useState(""); const [loggingOut, setLoggingOut] = useState(false);
  useEffect(() => { api<Me>("/api/v1/auth/me").then(setMe).catch(() => setMe(null)).finally(() => setChecking(false)); }, []);
  const logout = async () => {
    if (loggingOut) return;
    setLoggingOut(true); setLogoutError("");
    try { await post("/api/v1/auth/logout", {}); setMe(null); }
    catch (reason) { setLogoutError(`Выход не подтверждён. ${reason instanceof Error ? reason.message : String(reason)}`); }
    finally { setLoggingOut(false); }
  };
  if (checking) return <div className="boot"><Brand /><Loader /></div>;
  return me ? <Console me={me} onLogout={logout} logoutError={logoutError} loggingOut={loggingOut} /> : <Login onLogin={setMe} />;
}

export default function App() {
  return commercialEnabled ? <CommercialApp login={onLogin => <Login onLogin={onLogin} />} team={(me, onLogout, logoutError, loggingOut) => <Console me={me} onLogout={onLogout} logoutError={logoutError} loggingOut={loggingOut} />} /> : <LegacyApp />;
}
